import { toast } from '@heroui/react'
import { create } from 'zustand'

import { authApi } from '@/api/auth.api'
import i18n from '@/i18n'
import type { LoginRequest } from '@/types/auth'
import type { User } from '@/types/user'

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  showGreeting: boolean;

  login: (data: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setShowGreeting: (show: boolean) => void;
}

export const useAuthStore = create<AuthStore>(set => ({
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  error: null,
  showGreeting: false,

  login: async data => {
    set({ isLoading: true, error: null })
    try {
      const response = await authApi.login(data)
      localStorage.setItem('token', response.AccessToken)
      set({ user: response.User, isAuthenticated: true, isLoading: false, isInitialized: true, showGreeting: true })
    } catch (err) {
      toast.danger(i18n.t('auth.toast.login_error'), { description: (err as Error).message })
      set({ error: (err as Error).message, isLoading: false })
      throw err
    }
  },

  logout: async () => {
    set({ isLoading: true })
    try {
      await authApi.logout()
    } catch (err) {
      console.error('Logout API error:', err)
    } finally {
      localStorage.removeItem('token')
      set({ user: null, isAuthenticated: false, isLoading: false, isInitialized: true, showGreeting: false })
    }
  },

  checkAuth: async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      set({ isAuthenticated: false, isInitialized: true, isLoading: false })
      return
    }

    set({ isLoading: true, error: null })
    try {
      const user = await authApi.me()
      set({ user, isAuthenticated: true, isInitialized: true, isLoading: false })
    } catch (err) {
      localStorage.removeItem('token')
      set({ user: null, isAuthenticated: false, isInitialized: true, isLoading: false, error: (err as Error).message })
    }
  },

  setShowGreeting: show => set({ showGreeting: show }),
}))
